import moment from "moment-timezone";
import { sessionService } from "redux-react-session";
import axios from "axios";
import { isValidPhoneNumber } from "react-phone-number-input/input";
import axiosRetry from "axios-retry";
import OSREC from "currencyformatter.js";
import { enc } from "crypto-js";
import { retrieveLaunchParams } from "@telegram-apps/sdk-react";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import {
  signInWithCustomToken,
  getAuth,
  signOut,
} from "firebase/auth/web-extension";

import { countries } from "./country";
import { store } from "../redux/store";
import { RESET_STORE } from "../redux/types";
import {
  lowerCaseLetter,
  min8Char,
  numberCharacters,
  sessionErrorMessage,
  somethingWrong,
  upperCaseLetter,
  wallet,
  advanceSettings,
  changeAccountName,
  settings,
  details,
  closeAccount,
  verification,
  requestSubmitted,
  requestAccepted,
  fiatCurrency,
  security,
  changePassword,
  pendingConfirmations,
  linkAlreadyVerifiedSubMsg,
  sats,
  lnurl,
  accountStatus as accountStatusTxt,
  history,
  smsAuthentication,
  authenticatorApp,
  supportMail,
  btc,
  ggc,
  play,
  usd,
  cash,
  validStartAmountMsg,
  validEndAmountMsg,
  validEndAmountGreaterStartAmountMsg,
  XAUT,
  speedFee,
  status,
} from "./messages";
import {
  setWalletCurrency,
  setRedirectUrl,
  showToast,
  setAccountRestrictedModalData,
  setAccountAsset,
  getMaintenanceStatus,
  openAppDetailModal,
  setAppDetails,
  openInstalledAppDialog,
} from "../redux/actions";
import { currency } from "./Currency";
import { app } from "../util/firebase";
import Text from "./text/Text";
import CustomDivider from "./divider/Divider";
import {
  authAppIcon,
  bitCoinSymbol,
  ethereumIcon,
  lightningIcons,
  premiumPlanBg,
  smsIcon,
  solanaIcon,
  tronIcon,
  tonIcon,
  verifiedPlanBg,
} from "./images";

export const defaultDateFormat = "DD/MM/YYYY";
const speedVersion = "2022-10-15";
export const noOfRecords = 20;
export const stringMaxLength = "55";
export const minReqYear = 2021;
export const lightningLabel = "lightning";
export const onChainLabel = "onchain";
export const ethereumLabel = "ethereum";
export const tronLabel = "tron";
export const solanaLabel = "solana";
export const tonLabel = "ton";
export const LS_FPJS_REQUEST_ID = "fpjs_requestId";
export const LS_FPJS_VISITOR_ID = "fpjs_visitorId";
export const newContactLabel = "new";
export const existingContactLabel = "existing";

export const dateTimeFormat = (timestamp, dateFormat, timezone) => {
  const momentObj = (time) => {
    if (time) {
      return timezone ? moment(time).tz(timezone) : moment(time);
    } else {
      return moment();
    }
  };

  const currentDate = momentObj().format(defaultDateFormat);
  const yesterday = momentObj().subtract(1, "days").format(defaultDateFormat);

  if (timestamp) {
    if (momentObj(timestamp).format(defaultDateFormat) === currentDate) {
      return `Today, ${momentObj(timestamp).format("hh:mm A")}`;
    } else if (momentObj(timestamp).format(defaultDateFormat) === yesterday) {
      return `Yesterday, ${momentObj(timestamp).format("hh:mm A")}`;
    } else if (dateFormat) {
      return momentObj(timestamp).format(dateFormat.toUpperCase() + ", h:mm A");
    } else {
      return momentObj(timestamp).format(defaultDateFormat + ", h:mm A");
    }
  }
};

export const isValidDate = (momentObj) => moment(momentObj).isValid();

export const isFutureDate = (momentObj) =>
  moment(momentObj).diff(moment().endOf("day")) > 0;

export const minDateYear = moment(
  `01-01-${minReqYear}T00:00:00.000`,
  "DD/MM/YYYY"
);

export const getTimezoneMomentObj = (time, timezone) => {
  return timezone ? moment(time).tz(timezone) : moment(time);
};

export const allowedImageTypes = ["jpeg", "jpg", "png", "jfif"];

export const preDefinedMessages = ["Food & Beverage", "Tip", "Donation"];

const allowedMethods = ["GET", "POST", "DELETE", "PUT"];

const openAPIEndPoints = [
  "/sso-login",
  "/sso-signup",
  "/login",
  "/signup",
  "/forgot-password",
  "/generate-token",
  "/resend-verification-code",
  "/verify-user",
  "/verify-email",
  "/verify-forgot-password",
  "/country?limit=300",
  "/referral/validate",
  // "/promo-code/validate",
];

const dynamicGetApiEndPoints = ["/link/"];

export const serverErrorStatusCodes = [500, 502, 503, 504];
export const errorStatusCodes = [400, 401, 402, 403, 404, 408, 415, 428, 429];
export const lightningFees = 3;
export const minAmountRequiredForStableCoins = 0.5;
export const minAmountRequiredForXAUT = 0.0002;
export const minAmountRequiredForXautReceive = 0.0002;
export const maxAmountRequiredForXautReceive = 1;
export const minAmountRequiredForUsdt = 0.01;
export const minimumLightningUsdtNetFee = 0.01;
export const maximumAmountForStableCoins = 10000;
export const maximumAmountForXAUT = 1;
export const maximumAmountStableCoinReceive = 5000000;
export const maxAmountAllowedSats = 500000000;
export const minAmountRequiredSatsAtReceive = 0;
export const swapSatsMinAmount = 20000;
export const swapSatsMaxAmount = 20000000;
export const stableCoinMinAmount = 10;
export const stableCoinMaxAmount = 10000;
export const CHAR_SET =
  "abcdefghijklmnopqrstuvwxyz0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const redirectAfterError = (reject) => {
  expireSession();
  store.dispatch(
    showToast({
      isToastOpen: true,
      toastMessage: somethingWrong,
      toastVariant: "error",
    })
  );
  reject(503);
};

const clearAllStorageExcept = (keysToKeep) => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);

    if (!key) continue;

    //don't delete specified keys form local storage
    const isAllowed = keysToKeep.some((prefix) => key.startsWith(prefix));

    if (!isAllowed) {
      localStorage.removeItem(key);
    }
  }
};

export const expireSession = async (isRedirect = true) => {
  const authObj = getAuth(app);
  await signOut(authObj);
  sessionService.deleteSession();
  sessionService.deleteUser();
  sessionStorage.clear();
  clearAllStorageExcept(["userFirstName", "sn_mapp_"]);
  store.dispatch({
    type: RESET_STORE,
  });
  if (isRedirect) {
    store.dispatch(setRedirectUrl("/"));
  }
};

const onlyAuthTokenAPIEndpoints = [
  "/login/mfa/phone-number/verify-otp",
  "/login/mfa/phone-number/resend-otp",
  "/login/mfa/phone-number",
  "/login/mfa/phone-number/verify",
  "/login/mfa/phone-number/resend-verification-code",
];

const getHeaders = async (
  method,
  path,
  data,
  file,
  fileName,
  isChangedBaseURL
) => {
  const host = process.env.REACT_APP_API_ENDPOINT;

  let jwtOptions = {
    method,
    url: isChangedBaseURL ? `${path}` : `${host}${path}`,
  };
  const formData = file ? new FormData() : null;
  if (file) {
    formData.append("file", file, fileName);
  }

  if (data || allowedMethods.includes(method)) {
    const speedUser = data?.speedUser;
    if (speedUser) {
      delete data.speedUser;
    }

    jwtOptions.data = file ? formData : data;
    jwtOptions.headers = jwtOptions.headers || {};
    jwtOptions.headers["Content-Type"] = file
      ? "multipart/form-data"
      : "application/json";
    jwtOptions.headers["Accept"] = "application/json";
    jwtOptions.headers["speed-source"] = "c915683f3ec888b8edcc7b06bd1428ec";
    jwtOptions.headers["visitor-id"] =
      localStorage.getItem(LS_FPJS_VISITOR_ID) || "";
    jwtOptions.headers["visitor-request-id"] =
      localStorage.getItem(LS_FPJS_REQUEST_ID) || "";

    if (path === "/promo-code/validate") {
      jwtOptions.headers["speed-livemode"] = JSON.parse(
        process.env.REACT_APP_LIVEMODE
      );
    }

    const isEndPointIncluded = (endPointsList, endPoint) =>
      endPointsList.includes(endPoint);

    const isOpenAPI =
      isEndPointIncluded(openAPIEndPoints, path) ||
      (method === "GET" &&
        dynamicGetApiEndPoints.some((endpoint) => path.includes(endpoint)));

    const isOnlyAuthRequired = isEndPointIncluded(
      onlyAuthTokenAPIEndpoints,
      path
    );

    if (!isOpenAPI) {
      await sessionService
        .loadSession()
        .then(async (session) => {
          if (session?.access_token) {
            jwtOptions.headers["Access"] = session.access_token;
            jwtOptions.headers["speed-version"] = speedVersion;
            jwtOptions.headers["speed-session"] = session.session;
          }
          if (session?.id_token) {
            jwtOptions.headers["Authorization"] = session.id_token;
          }

          if (!isOnlyAuthRequired) {
            jwtOptions.headers["speed-livemode"] = JSON.parse(
              process.env.REACT_APP_LIVEMODE
            );
            jwtOptions.headers["speed-account-type"] = "wallet";
            jwtOptions.headers["speed-client-platform"] = "webwallet";

            if (session?.account?.account?.id) {
              jwtOptions.headers["speed-account"] = session.account.account.id;
            }
          }
        })
        .catch((_err) => {
          jwtOptions = {};
          expireSession();
        });
    }
  }
  return jwtOptions;
};

export const generateToken = async () => {
  const session = await sessionService.loadSession();
  return new Promise(async (resolve, reject) => {
    return await callAPIInterface(
      "POST",
      "/generate-token",
      JSON.stringify({
        refresh_token: session.refresh_token,
      })
    )
      .then(async (response) => {
        const updatedSession = {
          refresh_token: session.refresh_token,
          access_token: response.access_token,
          id_token: response.id_token,
          timestamp: getTimestamp(),
        };
        await sessionService.saveSession({ ...session, ...updatedSession });
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const callAPIInterface = (
  method,
  path,
  data = "",
  file = null,
  fileName = null,
  isChangedBaseURL = false
) => {
  return new Promise(async (resolve, reject) => {
    let jwtOptions = await getHeaders(
      method,
      path,
      data,
      file,
      fileName,
      isChangedBaseURL
    );

    if (Object.keys(jwtOptions)?.length) {
      let apiCall = axios({
        ...jwtOptions,
      });
      apiCall
        .then((res) => {
          resolve(res?.data);
        })
        .catch(async (err) => {
          if (err.response) {
            const errors = err.response.data.errors;
            const errorStatus = err.response.status;
            const errorType =
              errors && errors[0].type && errors[0].type.toLowerCase();

            if (serverErrorStatusCodes.includes(errorStatus)) {
              await expireSession();
              errors &&
                errors[0].message &&
                store.dispatch(
                  showToast({
                    isToastOpen: true,
                    toastMessage: errors[0].message,
                    toastVariant: "error",
                  })
                );
              reject(errorStatus);
            } else if (errorStatusCodes.includes(errorStatus)) {
              const isAccountRestricted = errorType === "account_restriction";

              const errorTypesArr = [
                "invalid_refresh_token",
                "token_expired",
                "invalid_permission",
              ];
              const restrictAPIPath = [
                "/link/",
                "/invite-users",
                "/signup-invite",
                "/referral/validate",
              ];

              let shouldShowToast =
                errorTypesArr.includes(errorType) ||
                restrictAPIPath.some((pathName) => path.includes(pathName));

              if (errorType === "token_expired" && path !== "/generate-token") {
                generateToken()
                  .then(async (res) => {
                    jwtOptions.headers["Access"] = res.access_token;
                    jwtOptions.headers["Authorization"] = res.id_token;
                    axiosRetry(
                      axios({
                        ...jwtOptions,
                      })
                        .then((response) => {
                          resolve(response?.data);
                        })
                        .catch(async (retryError) => {
                          // This is for: After new tokens were generated if any error will be occurred in the current API then the user will be logged out.
                          const pathNotRedirectToLogin = [
                            "/search",
                            "/mfa",
                            "/verify-user-password",
                          ];
                          if (
                            pathNotRedirectToLogin.some((pathName) => {
                              return path.includes(pathName);
                            })
                          ) {
                            reject(retryError);
                          } else {
                            expireSession();
                            path !== "/logout" &&
                              store.dispatch(
                                showToast({
                                  isToastOpen: true,
                                  toastMessage: sessionErrorMessage,
                                  toastVariant: "error",
                                })
                              );
                          }
                        }),
                      { retries: 1 }
                    );
                  })
                  .catch((tokenError) => {
                    const tokenErrors =
                      tokenError &&
                      tokenError.response &&
                      tokenError.response.data.errors[0];
                    if (tokenErrors) {
                      // If any error occurs during generate token then user will be logged out.
                      expireSession();
                      path !== "/logout" &&
                        store.dispatch(
                          showToast({
                            isToastOpen: true,
                            toastMessage: sessionErrorMessage,
                            toastVariant: "error",
                          })
                        );
                    }
                  });
              } else if (errorType === "unauthorized") {
                expireSession();
              } else if (isAccountRestricted) {
                shouldShowToast = true;
                store.dispatch(
                  setAccountRestrictedModalData({
                    openRestrictedModal: true,
                    errorMessage: errors?.[0]?.message,
                    callbackErrorFn: () => reject(err),
                  })
                );
              } else if (errorType === "invalid_permission") {
                store.dispatch(setRedirectUrl("/account-suspended"));
              } else {
                reject(err);
              }

              errors &&
                errors[0].message &&
                !shouldShowToast &&
                (await store.dispatch(
                  showToast({
                    isToastOpen: true,
                    toastMessage: errors[0].message,
                    toastVariant: "error",
                  })
                ));
            } else {
              redirectAfterError(reject);
            }
          } else if (err.request) {
            redirectAfterError(reject);
          }
        });
    } else {
      reject({});
    }
  });
};

export const verifyUser = (data) =>
  callAPIInterface("POST", "/verify-user", JSON.stringify(data))
    .then((response) => response)
    .catch((e) => e);

export const getFirestoreData = (user) => {
  const db = getFirestore(app);
  const userRef = doc(db, "user", user.id);
  try {
    onSnapshot(userRef, async (querySnapshot) => {
      const data = querySnapshot.data();
      if (data) {
        const session = await sessionService
          .loadSession()
          .then((res) => res)
          .catch((e) => {});
        const timestamp = session && session.timestamp;

        if (
          data.global_sign_out_timestamp &&
          timestamp < data.global_sign_out_timestamp
        ) {
          const eventSession = sessionStorage.getItem("mapp_session");

          if (eventSession) {
            const appData = store.getState()?.common?.appDetails?.data;

            try {
              localStorage.setItem(
                eventSession,
                JSON.stringify({
                  session: eventSession,
                  app_id: appData?.id,
                  app_name: appData?.display_name,
                  close_time: Date.now(),
                })
              );
            } catch (e) {
              console.warn("Failed to persist analytics before sign-out:", e);
            }
          }

          expireSession();
        }
      }
    });
  } catch (_e) {}
};

export const getUser = () =>
  callAPIInterface("GET", "/user")
    .then(async (response) => {
      const token = await getFirebaseTokenForAccount();
      const authObj = getAuth(app);
      token?.firebase_token &&
        signInWithCustomToken(authObj, token.firebase_token)
          .then(() => {
            getFirestoreData(response);
          })
          .catch((_error) => {});

      return response;
    })
    .catch((e) => e);

export const getTimestamp = () => {
  const date = new Date();
  date.setSeconds(date.getSeconds() + 10);
  return date.getTime();
};

export const char8 = /(?=.{8,})/;
export const upperCase = /(?=.*[A-Z])/;
export const lowerCase = /^(?=.*[a-z])/;
export const number = /(?=.*[0-9])/;
export const isAlphabet = /^[aA-zZ\s]+$/;

export const passwordPolicyState = (value) => {
  let currentState = [];
  char8.test(value)
    ? currentState.push(min8Char)
    : currentState.filter((item) => item !== min8Char);
  upperCase.test(value)
    ? currentState.push(upperCaseLetter)
    : currentState.filter((item) => item !== upperCaseLetter);
  lowerCase.test(value)
    ? currentState.push(lowerCaseLetter)
    : currentState.filter((item) => item !== lowerCaseLetter);
  number.test(value)
    ? currentState.push(numberCharacters)
    : currentState.filter((item) => item !== numberCharacters);

  return currentState;
};

export const passwordPolicy = [
  min8Char,
  upperCaseLetter,
  lowerCaseLetter,
  numberCharacters,
];

export const getCountryObj = (key, value, countryList = countries) => {
  return countryList.find((country) => country[key] === value);
};

export const validatePhoneNumber = (phoneNumber) => {
  if (phoneNumber) {
    try {
      return isValidPhoneNumber(phoneNumber);
    } catch (e) {
      return false;
    }
  } else {
    return true;
  }
};

export const getCurrentTimeInSecond = (expiresTime) => {
  const seconds = Math.floor(
    (new Date(expiresTime).getTime() - new Date().getTime()) / 1000
  );
  return seconds > 0 ? seconds : 0;
};

export const logOut = async () => {
  const session = await sessionService.loadSession();
  return await callAPIInterface("POST", "/logout", JSON.stringify(session))
    .then(() => {
      expireSession();
    })
    .catch(() => {});
};

export const setUpdatedSessionData = async (response, authData = null) => {
  let currentSession = null;
  await sessionService
    .loadSession()
    .then((res) => (currentSession = res))
    .catch((_e) => {});

  const updatedSession = {
    ...currentSession,
    ...response,
    last_action_time: moment().valueOf(),
    ...(authData?.email && { email: authData.email }),
  };
  await sessionService.saveSession(updatedSession);
};

export const getDefaultCurrencyBasedOnCountry = (walletAccounts) =>
  currency.find(
    (x) =>
      x?.code ===
        countries.find(
          (country) =>
            walletAccounts?.[0]?.account?.country === country.short_name
        )?.preferred_currency_code || x.code === "USD"
  );

export const handleAfterLoginProcess = async (
  navigate,
  isFromVerifyEmailPage = false
) => {
  try {
    const currentSession = await sessionService.loadSession();

    const user = await getUser();

    currentSession["user"] = user;
    await sessionService.saveSession(currentSession);

    localStorage.setItem("userFirstName", user?.first_name);

    const walletAccounts = user?.accounts_details?.filter(
      (accountData) => accountData?.account?.account_type === wallet
    );

    let redirectToDashboard = true;
    if (isFromVerifyEmailPage) {
      const countries = await fetchCountries();
      if (isPhoneVerificationRequired(user, countries)) {
        navigate("/phone-number-verification", {
          state: { isFromVerifyEmailPage: true },
        });
        redirectToDashboard = false;
      }
    }

    if (walletAccounts?.length > 0) {
      currentSession["account"] = walletAccounts[0];
      store.dispatch(
        setWalletCurrency(getDefaultCurrencyBasedOnCountry(walletAccounts))
      );
    }

    await sessionService.saveSession(currentSession);
    if (walletAccounts?.length > 0) {
      store.dispatch(setAccountAsset(true, redirectToDashboard));
      store.dispatch(getMaintenanceStatus({ afterLogin: true }));
    } else {
      navigate("/create-wallet-consent");
    }
  } catch (error) {
    expireSession();
  }
};

export const getMultilevelCountryCallingCode = (callingCode) => {
  if (callingCode) {
    const countPlus = callingCode
      ?.split("")
      ?.filter((char) => char === "+")?.length;

    return countPlus > 0 ||
      (callingCode?.[0] === "1" && callingCode?.length > 1)
      ? "1"
      : callingCode;
  }
};

export const concatenatedMobileNumber = (callingCode, mobileNumber) => {
  return callingCode && mobileNumber
    ? `+${getMultilevelCountryCallingCode(callingCode) + mobileNumber}`
    : "";
};

export const mobileNumberWithoutCallingCode = (callingCode, mobileNumber) => {
  return mobileNumber?.replace(
    `+${getMultilevelCountryCallingCode(callingCode)}`,
    ""
  );
};

export const getIsAuthenticated = (session) => {
  if (!session) {
    return false;
  }

  return session.access_token && session.id_token && session.account
    ? true
    : false;
};
export const getNumberFormatAccordingToCurrency = (amount, currency) => {
  const currenciesSupportedByOSREC = Object.keys(OSREC?.symbols);
  if (currency === XAUT) {
    return Number(amount).toFixed(6);
  }
  const params = {
    currency: currenciesSupportedByOSREC?.includes(currency) ? currency : "USD",
    symbol: "",
    valueOnError: amount,
  };

  return OSREC?.format(amount, params) || amount;
};

export const getCurrencyObj = (code) => {
  return currency?.find((cur) => cur.code === code?.toUpperCase());
};

export const getDisplayCurrencySymbol = (currencyCode, showBtcAmount) =>
  getCurrencyObj(currencyCode === sats && showBtcAmount ? btc : currencyCode)?.symbol || "";

export const getAmountWithCurrency = (currency, amount) => {
  return amount ? `${getCurrencyObj(currency).symbol} ${amount}` : "0.00";
};

export const getPaidPaymentMethod = (rowItem) => {
  if (rowItem?.status === "paid" && rowItem?.object === "payment") {
    if (
      rowItem?.target_amount_paid_by ===
      rowItem?.payment_method_options?.lightning?.id
    ) {
      return lightningLabel;
    } else if (
      rowItem?.target_amount_paid_by ===
      rowItem?.payment_method_options?.on_chain?.id
    ) {
      return onChainLabel;
    } else if (
      rowItem?.target_amount_paid_by ===
      rowItem?.payment_method_options?.ethereum?.id
    ) {
      return ethereumLabel;
    } else if (
      rowItem?.target_amount_paid_by ===
      rowItem?.payment_method_options?.tron?.id
    ) {
      return tronLabel;
    } else if (
      rowItem?.target_amount_paid_by ===
      rowItem?.payment_method_options?.solana?.id
    ) {
      return solanaLabel;
    } else if (
      rowItem?.target_amount_paid_by ===
      rowItem?.payment_method_options?.ton?.id
    ) {
      return tonLabel;
    }
  } else if (
    rowItem?.object === "withdraw" ||
    rowItem?.object === "instant_send"
  ) {
    return rowItem?.withdraw_method || "-";
  } else if (rowItem?.object === "swap") {
    return rowItem?.target_currency_swap_in;
  }
  return "-";
};

export const fetchUnconfirmedPaymentTransactions = async (limit, currency) => {
  const data = {
    status: ["paid"],
    limit,
    confirmations: { eq: 0 },
    target_currency: [currency],
  };
  try {
    return await callAPIInterface("POST", "/payments/filter", data);
  } catch {
    return { data: [] };
  }
};

export const fetchUnconfirmedWithdrawTransactions = async (limit, currency) => {
  const data = {
    status: ["unpaid"],
    limit,
    target_currency: [currency],
  };
  try {
    return await callAPIInterface("POST", "/withdraws/filter", data);
  } catch {
    return { data: [] };
  }
};

export const fetchPendingSwaps = async (limit, currency) => {
  const data = {
    admin_status: ["PENDING"],
    confirmations: { eq: 0 },
    limit,
    target_currency_swap_in: [currency],
  };
  try {
    return await callAPIInterface("POST", "/balances/swap/filter", data);
  } catch {
    return { data: [] };
  }
};

export const convertExponentialToDecimal = (exponentialNumber) => {
  const str = exponentialNumber?.toString();
  if (str?.indexOf("e") !== -1) {
    let extraExponentDigits = str.includes(".")
      ? str.indexOf("e") - str.indexOf(".") - 1
      : 0;
    const exponent = Math.abs(
      parseInt(str.substring(str.indexOf("e") + 1), 10)
    );
    return exponentialNumber.toFixed(exponent + extraExponentDigits);
  } else {
    return exponentialNumber;
  }
};

export const regexToTruncateZeros = /(\.\d*[1-9])0+$|\.0*$/;

export const getAmountFormatted = (amount, currency) => {
  const amountInBtc = store.getState()?.common?.showBtcAmount;

  return amountInBtc && currency === sats
    ? convertExponentialToDecimal(+Number(amount).toFixed(8))
    : getNumberFormatAccordingToCurrency(
        convertExponentialToDecimal(+Number(amount).toFixed(8)),
        currency
      ).replace(regexToTruncateZeros, "$1");
};

export const satsToBtcAmount = (satAmount) => {
  return convertExponentialToDecimal(
    +(Math.floor(satAmount) / 100000000).toFixed(8)
  );
};

export const btcToSatsAmount = (btcAmount) => {
  return convertExponentialToDecimal(+(btcAmount * 100000000).toFixed(8));
};

export const getExchangeRate = (
  currency,
  targetCurrency,
  targetCurrencyDenomination = ""
) => {
  const data = {
    currency: currency,
    target_currency: targetCurrency,
  };

  if (targetCurrencyDenomination) {
    data.target_currency_denomination = targetCurrencyDenomination;
  }

  return new Promise((resolve, reject) => {
    callAPIInterface("POST", "/utility/exchange-rate", data)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const satsToFiatAmount = (satsValue, exchangeRate) => {
  return parseFloat((satsValue / exchangeRate).toFixed(2));
};

export const displayAmountWithCurrency = (
  currency,
  amount,
  isNegativeAmount = false
) => {
  const symbol = getCurrencyObj(currency)?.symbol || "";
  return (
    <>
      {isNegativeAmount && "-"}
      {symbol}
      {amount}
    </>
  );
};

export const getFirebaseTokenForAccount = async () => {
  try {
    const res = await callAPIInterface("GET", "/account/firebase-token");
    return res;
  } catch (err) {
    throw err;
  }
};

export const routeBreadcrumbs = [
  {
    title: settings,
    route: "/settings",
  },
  {
    title: advanceSettings,
    route: "/settings/advance-settings",
  },
  {
    title: changeAccountName,
    route: "/settings/advance-settings/change-account-name",
  },
  {
    title: accountStatusTxt,
    route: "/settings/advance-settings/permission",
  },
  {
    title: closeAccount,
    route: "/settings/advance-settings/close-account",
  },
  {
    title: verification,
    route: "/settings/advance-settings/close-account/verification",
  },
  {
    title: requestSubmitted,
    route: "/settings/advance-settings/close-account/request-submitted",
  },
  {
    title: requestAccepted,
    route: "/settings/advance-settings/close-account/request-accepted",
  },
  {
    title: fiatCurrency,
    route: "/settings/fiat-currency",
  },
  {
    title: security,
    route: "/settings/security",
  },
  {
    title: changePassword,
    route: "/settings/security/change-password",
  },
  { title: history, route: "/history" },
  { title: details, route: "/history/:id" },
  { title: pendingConfirmations, route: "/pending-confirmations" },
  { title: details, route: "/pending-confirmations/:id" },
];

export const generateBreadCrumbData = (props, breadcrumbData, params = {}) => {
  //to get match path
  const result = Object.keys(params).length
    ? Object.entries(params).reduce((path, [key, value]) => {
        return path.replace(`/${value}`, `/:${key}`);
      }, props.pathname)
    : null;

  return breadcrumbData
    .filter(
      ({ route }) => props.pathname.includes(route) || result?.includes(route)
    )
    .map(({ route, ...rest }) => ({
      route: Object.keys(params).length
        ? Object.keys(params).reduce(
            (newRoute, param) => newRoute.replace(`:${param}`, params[param]),
            route
          )
        : route,
      ...rest,
    }));
};

export const updateUserAndWalletAccountSession = async () => {
  const user = await getUser();
  const currentSession = await sessionService.loadSession();
  const walletAccounts = user?.accounts_details?.filter(
    (accountData) => accountData?.account?.account_type === wallet
  );
  await sessionService.saveSession({
    ...currentSession,
    user: user,
    ...(walletAccounts?.length > 0 && { account: walletAccounts[0] }),
  });
};
export const sourceTelegramApp = "TelegramApp";
export const sourceWebWallet = "WebWallet";
export const payRequest = "payRequest";
export const withdrawRequest = "withdrawRequest";

export const networkFeesArr = ["Network Fees", "Network Fees Reversal"];

export const extractParamsRegex = /([?&])([^=]+)=([^&#;\s]+)/g;
export const extractAddressRegex = /[?#&=]/g;
export const validAddress = /^[\w?:&=#.\-@]+$/;
export const userNameRegex = /^[a-z0-9.\-_]+$/;
export const extractSecondAddress = /[=:?]/g;
export const emailAddressRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const removeNonEngChar = /[^\w?:&=#.@\-+/]\s*/g;

export const getDisplayAmount = (isCurrencyExchanged, amount, code) => {
  let formattedAmt = 0;
  const amountInBtc = store.getState()?.common?.showBtcAmount;
  let amt = Number(amount);
  if (!isNaN(amt)) {
    if (isCurrencyExchanged) {
      if (amt === 0) {
        // Show 0 if the fiat amount is 0
      } else {
        formattedAmt = getNumberFormatAccordingToCurrency(amt, code);
      }
    } else {
      if (code === sats) {
        if (amountInBtc) {
          const btcAmount = amt
            ?.toString()
            ?.replace(regexToTruncateZeros, "$1");
          return convertExponentialToDecimal(+btcAmount);
        } else {
          amt = amt.toFixed(0);
        }
      }

      formattedAmt = getNumberFormatAccordingToCurrency(amt, code);
    }
  }

  formattedAmt = formattedAmt.toString();
  return formattedAmt?.replace(regexToTruncateZeros, "$1");
};

export const makeBoldString = (data) => {
  const address = data?.slice(0, 12) + "..." + data?.slice(-12);
  const boldStart = address.substring(0, 4);
  const boldEnd = address.substring(address.length - 4);
  const middle = address.substring(4, address.length - 4);
  return (
    <>
      <span style={{ color: "#0A193E", fontWeight: "600" }}>{boldStart}</span>
      {middle}
      <span style={{ color: "#0A193E", fontWeight: "600" }}>{boldEnd}</span>
    </>
  );
};

export const createRedirectionAuthURL = (authType, state = {}) => {
  const redirectURL = new URL(
    authType === "google"
      ? process.env.REACT_APP_GOOGLE_SSO_URL
      : process.env.REACT_APP_APPLE_SSO_URL
  );

  if (Object.keys(state).length) {
    const rawStr = JSON.stringify(state);
    const encodedState = enc.Base64.stringify(enc.Utf8.parse(rawStr));
    redirectURL.searchParams.append("state", encodedState);
  }

  return redirectURL;
};

export const transactionTypeValues = {
  Payment: "Received",
  "Network Fees": "Network fee",
  Withdraw: "Sent",
  "Withdraw Reversal": "Refund",
  "Network Fees Reversal": "Network fee refund",
  Unconfirmed: "Receiving...",
  "Referee Reward In": "Referral bonus",
};

export const linkAlreadySubText = (email) => (
  <>
    {linkAlreadyVerifiedSubMsg[0]}{" "}
    <Text size={16} font="semibold" className="default-text" variant="inline">
      {email}{" "}
    </Text>
    {linkAlreadyVerifiedSubMsg[1]}
  </>
);

export const checkLinkStatus = async (link) => {
  try {
    const response = await callAPIInterface("GET", `/link/${link}`);
    return response;
  } catch (error) {
    console.error(error);
    return error;
  }
};

export const displayTaprootCurrency = (amount, currency) => {
  const amountInBtc = store.getState()?.common?.showBtcAmount;
  if (currency === sats) {
    if (amountInBtc) {
      return "BTC";
    } else {
      return sats;
    }
  } else if (currency === XAUT) {
    return XAUT;
  } else {
    return currency;
  }
};

export const handleSSOLoginRedirection = async ({
  authMethod,
  inviteeAuthState,
  localhostSsoFe = false,
}) => {
  if (localhostSsoFe) {
    inviteeAuthState = {
      ...inviteeAuthState,
      redirectToLocalhostUrl: true,
    };
  }
  const redirectionURL = createRedirectionAuthURL(authMethod, inviteeAuthState);
  window.open(redirectionURL, "_self");
};

export const authOptionsFooterDivider = (
  <CustomDivider className="auth-divider" />
);

export const receiveViaMethod = (method, address) => {
  let methodType = "Receive via ";
  if (method === lnurl) {
    if (emailAddressRegex.test(address)) {
      methodType += " LN Address";
    } else if (address.includes("lnurl")) {
      methodType += " LN URL";
    }
  }
  return methodType;
};
export const sendViaMethod = (method, address) => {
  let methodType = "Send via";
  switch (method) {
    case "onchain":
      methodType += " Onchain";
      break;
    case "lightning":
      methodType += " Lightning";
      break;
    case "lnurl":
      if (emailAddressRegex.test(address)) {
        methodType += " LN Address";
      } else if (address.includes("lnurl")) {
        methodType += " LN URL";
      } else {
        methodType += " LN URL";
      }
      break;
    case "ethereum":
      methodType += " Ethereum";
      break;
    case "tron":
      methodType += " Tron";
      break;
    case "solana":
      methodType += " Solana";
      break;
    case "ton":
      methodType += " Ton";
      break;
    default:
      methodType += "";
      break;
  }
  return methodType;
};

export const renderReceiveHeader = (currency) =>
  `Receive ${currency === "SATS" ? "BTC" : currency}`;

export const allowedLNurl = [payRequest, withdrawRequest];

export const fetchPrecisedInteger = (num, precision) => {
  if (!num || !Number(num)) return 0;

  const regex = new RegExp(`^-?\\d+(?:\\.\\d{0,${precision}})?`);
  let res = num?.toString()?.match(regex);
  return res?.[0] ? res[0].replace(regexToTruncateZeros, "$1") : num;
};

export const getAccountFees = async (method, currency, transactionCode) => {
  try {
    const params = `?payment_method=${method}&transaction_code=${transactionCode}&target_currency=${currency}`;
    const speedFees = await callAPIInterface("GET", `/account-fees` + params);
    return {
      feePercentage: speedFees?.fee_percentage ?? 0,
      fixedFee: speedFees?.fee_fixed ?? 0,
    };
  } catch (err) {
    return {
      fee_percentage: 0,
      fee_fixed: 0,
    };
  }
};

export const fetchAllAssets = async () => {
  try {
    const response = await callAPIInterface("GET", `/assets-account?limit=20`);
    return response?.data.sort((a, b) => a.created - b.created);
  } catch (err) {}
};

export const convertTimezoneToEpochFormat = (date) =>
  +getTimezoneMomentObj(date).startOf("day").format("x");

export const filterDataFrom = ["type", "target_currency", "created"];

export const displayAmount = (
  amount,
  exchangeRate,
  targetedCurrency,
  currency,
  checkExchange
) => {
  const showBtcAmount = store.getState()?.common?.showBtcAmount;
  let fiatAmount = 0;
  let convertedFiatAmount = 0;
  if (checkExchange) {
    if (showBtcAmount && currency === sats) {
      convertedFiatAmount = btcToSatsAmount(amount) / exchangeRate;
      fiatAmount = fetchPrecisedInteger(convertedFiatAmount, 2);
    } else {
      convertedFiatAmount = amount / exchangeRate;
      fiatAmount = fetchPrecisedInteger(convertedFiatAmount, 2);
    }

    if (fiatAmount === "0") {
      fiatAmount = convertedFiatAmount?.toFixed(currency === sats ? 8 : 2);
    }

    return getDisplayAmount(checkExchange, fiatAmount, targetedCurrency);
  } else {
    return getDisplayAmount(checkExchange, amount, currency);
  }
};

export const accountStatus = {
  active: {
    label: "Active",
    variant: "primary",
    value: "active",
  },
  restricted: {
    label: "Restricted",
    variant: "warning",
    value: "restricted",
  },
  suspended: {
    label: "Suspended",
    variant: "warning",
    value: "suspended",
  },
  closure_in_progress: {
    label: "Closure Requested",
    variant: "error-primary",
    value: "closure_in_progress",
  },
  limited_restriction: {
    label: "Limited Restriction",
    variant: "warning",
    value: "limited_restriction",
  },
  closed: {
    label: "Closed",
    variant: "default",
    value: "closed",
  },
};

export const restrictedModulesList = [
  {
    module: "CREATE_PAYMENT",
    name: "Receive",
    is_restricted: false,
    icon: "south_west",
  },
  {
    module: "CREATE_WITHDRAW",
    name: "Send",
    is_restricted: false,
    icon: "north_east",
  },
  {
    module: "SWAP",
    name: "Swap",
    is_restricted: false,
    icon: "swap_horiz",
  },
];

export const captchaDigit = 6;
export const captchaBackgroundColor = "#F0F5FA";

export const tapRootMethods = {
  SATS: ["onchain", "lightning", "lnurl"],
  USDT: ["onchain", "ethereum"],
};

export const generateAmountToPass = (
  amount,
  currency,
  isLightningUsdt = false
) => {
  const showBtcAmount = store.getState()?.common?.showBtcAmount;
  let newAmount;
  switch (currency) {
    case sats:
      if (showBtcAmount && !isLightningUsdt) {
        newAmount = btcToSatsAmount(amount);
      } else {
        newAmount = Math.round(amount);
      }
      break;
    case XAUT:
      newAmount = Number(amount).toFixed(6);
      break;
    default:
      newAmount = Number(amount).toFixed(2);
  }
  return Number(newAmount);
};

export const getCurrencyAmount = (amount, currency = null) => {
  let tapCurrency = currency || store.getState()?.common?.taprootAsset?.uuid;
  const amountInBtc = store.getState()?.common?.showBtcAmount;
  if (tapCurrency === sats) {
    return amountInBtc ? satsToBtcAmount(amount) : Math.floor(amount);
  } else if (stableCoins.includes(tapCurrency)) {
    return amount / 100;
  } else if (tapCurrency === XAUT) {
    return amount / 1000000;
  } else {
    return amount;
  }
};

export const eth = "ETH";
export const walletConst = "WALLET";
export const paymentConst = "PAYMENT";
export const withdrawConst = "WITHDRAW";
export const swapConst = "SWAP";
export const trx = "TRX";
export const lnAddressConst = "PAY_REQUEST";

export const updateSessionData = async (currentSession) => {
  const user = await getUser();
  const walletAccounts = user?.accounts_details?.filter(
    (accountData) => accountData?.account?.account_type === wallet
  );
  const updatedSession = {
    ...currentSession,
    user: user,
    account: walletAccounts[0],
  };

  await sessionService.saveSession(updatedSession);
  return updatedSession;
};

export const supportedTaprootAssets = ["SATS", "USDT", "USDC", "XAUT"];

export const stableCoins = ["USDC", "USDT"];

export const depositSupportedCountries = ["US"];

export const prepareTabDataInOrder = (data) => {
  let result = supportedTaprootAssets?.map((asset) =>
    data?.find((d) => d?.uuid === asset)
  );
  result = sortInAscendingManner(result, "weight");

  return result.filter(Boolean);
};

export const BTCToFiatAmount = (amount, exchangeRate) => {
  const satsAmount = btcToSatsAmount(amount);
  const fiatAmount = satsAmount / exchangeRate;
  let tempConvertedAmount = fetchPrecisedInteger(fiatAmount, 2);
  if (tempConvertedAmount === "0") {
    tempConvertedAmount = fiatAmount.toFixed(8);
  }
  return tempConvertedAmount;
};

export const handleAmountChangeCommon = (
  enteredAmount,
  checkExchange,
  exchangeRate,
  currency,
  setAmount,
  setConvertedAmount
) => {
  const showBtcAmount = store.getState()?.common?.showBtcAmount;

  const amount = showBtcAmount ? enteredAmount?.slice(0, 10) : enteredAmount;

  let toFixedAmount;

  switch (currency) {
    case sats:
      toFixedAmount = 8;
      break;
    case XAUT:
      toFixedAmount = 6;
      break;
    default:
      toFixedAmount = 2;
  }

  if (showBtcAmount && amount.toString()?.length > 0 && currency === sats) {
    const amt = amount;
    if (checkExchange) {
      const mainAmount = amt * exchangeRate;
      setConvertedAmount(amt);
      setAmount(satsToBtcAmount(mainAmount));
    } else {
      setAmount(convertExponentialToDecimal(amt));
      setConvertedAmount(BTCToFiatAmount(amt, exchangeRate));
    }
  } else {
    if (amount.toString().length <= 10) {
      const isNumeric = !isNaN(amount) && !isNaN(parseFloat(amount));
      const amt = !isNumeric
        ? amount
        : checkExchange || currency !== sats
          ? Math.abs(amount)
          : Math.abs(amount).toFixed(0);

      if (checkExchange) {
        setConvertedAmount(amt);
        setAmount((amt * exchangeRate).toFixed(toFixedAmount));
      } else {
        setAmount(amt);
        let tempConvertedAmount = fetchPrecisedInteger(amt / exchangeRate, 2);
        if (tempConvertedAmount === "0") {
          tempConvertedAmount = (amt / exchangeRate).toFixed(toFixedAmount);
        }
        setConvertedAmount(tempConvertedAmount);
      }
    }
  }
};

export const displaySelectedCurrencyAmount = (amount) => {
  const showBtcAmount = store.getState()?.common?.showBtcAmount;
  return showBtcAmount ? satsToBtcAmount(amount) : amount;
};

export const defaultPaymentMethods = {
  SATS: ["lightning", "onchain"],
  USDT: ["ethereum"],
  USDC: ["solana"],
  XAUT: ["ethereum"],
};

export const maintenanceStatus = {
  operational: "operational",
  under_maintenance: "under_maintenance",
  scheduled: "scheduled",
};

export const sortInAscendingManner = (array, field) => {
  return array?.sort((a, b) => a[field] - b[field]);
};

const scaleFactor = {
  SATS: {
    SATS: 1,
    BTC: 0.00000001,
  },
  USDT: {
    USDT: 0.01,
  },
  USDC: {
    USDC: 0.01,
  },
  BTC: {
    SATS: 100_000_000,
    BTC: 1,
  },
  USD: {
    USD: 0.01,
  },
  XAUT: {
    XAUT: 0.000001,
  },
};

export const showAmount = ({
  amount,
  currency,
  multiplier = null,
  showCommaSeparated = false,
  targetedCurrency = currency,
  appendCurrency = false,
  divideAmount = false,
  showMoreDigitIfZero = false,
}) => {
  const scale = multiplier || scaleFactor[currency]?.[targetedCurrency] || 1;
  let finalAmount = divideAmount ? amount / scale : amount * scale;

  finalAmount = finalAmount.toFixed(8);

  let formattedAmount =
    showCommaSeparated && targetedCurrency !== "BTC"
      ? getNumberFormatAccordingToCurrency(finalAmount, targetedCurrency)
      : finalAmount;

  formattedAmount =
    showMoreDigitIfZero && formattedAmount === "0.00"
      ? finalAmount
      : formattedAmount.replace(regexToTruncateZeros, "$1");

  return appendCurrency
    ? displayAmountWithCurrency(targetedCurrency, formattedAmount)
    : formattedAmount;
};

export const decodeBase64EncodedString = (encodedString) => {
  if (encodedString) {
    const decodedString = enc.Utf8.stringify(enc.Base64.parse(encodedString));
    return JSON.parse(decodedString);
  }
  return encodedString;
};

export const ssoData = (location, search) => {
  const getURLData = (keyName) => new URLSearchParams(search).get(keyName);
  const signupMethod =
    location?.pathname === "/google-auth" ? "google" : "apple";
  const authCode = getURLData("code");
  const decodedAuthState = decodeBase64EncodedString(getURLData("state"));
  const userData = JSON.parse(decodeURIComponent(getURLData("user")));
  return { signupMethod, authCode, decodedAuthState, userData };
};

export const apiCallSSOCommon = ({
  decodedAuthState,
  redirectFn,
  authCode,
  signupMethod,
  userData,
}) => {
  const { redirectToLocalhostUrl } = decodedAuthState || {};
  const localhostUrl = "http://localhost:3000";
  const isRedirectToLocalhost = window.location.origin !== localhostUrl;

  //We are applying isRedirectToLocalhost in condition to prevent looping once redirect to localhost from app urls
  if (isRedirectToLocalhost && redirectToLocalhostUrl) {
    const localhostRedirectUrl = window.location.href?.replace(
      process.env.REACT_APP_URL,
      localhostUrl
    );
    window.location.assign(localhostRedirectUrl);
    return;
  }

  if (!authCode) {
    redirectFn();
    return;
  }

  const apiPayload = {
    auth_code: authCode,
    signup_method: signupMethod,
    source: isTMA() ? sourceTelegramApp : sourceWebWallet,
  };

  if (signupMethod === "apple" && userData) {
    apiPayload.first_name = userData.name?.firstName;
    apiPayload.last_name = userData.name?.lastName;
  }
  return apiPayload;
};

export const addressAutoCompleteOptions = [
  "speed.app",
  "tryspeed.com",
  "walletofsatoshi.com",
  "zbd.gg",
  "getalby.com",
];

export const getCurrentAccountCountryCode = async () => {
  const session = await sessionService.loadSession();
  const accountCountry = session?.account?.account?.country;
  const countryCode = countries.find(
    (country) => country.short_name === accountCountry
  )?.iso2;
  return countryCode;
};

export const isTMA = () => {
  try {
    retrieveLaunchParams();
    return true;
  } catch {
    return false;
  }
};

export const displayPhoneNumber = (callingCode, phoneNumber) => {
  const lastFour = phoneNumber?.slice(-4);
  const masked = phoneNumber?.slice(0, -4).replace(/\d/g, "*");
  const phoneStr = `+${callingCode} ${masked}${lastFour}`;
  return phoneStr;
};

export const formatDisplayValues = ({
  amount,
  currency,
  targetedCurrency,
  multiplier,
}) => {
  const baseOptions = { amount, currency, targetedCurrency, multiplier };

  return {
    withComma: showAmount({ ...baseOptions, showCommaSeparated: true }),
    withoutComma: +showAmount({ ...baseOptions, showCommaSeparated: false }),
  };
};

export const getTimeZoneAndDateFormat = () => {
  const userGlobalState = store.getState()?.auth?.user;
  const dateFormat = userGlobalState?.date_format;
  const timezone =
    userGlobalState?.timezone ||
    userGlobalState?.accounts_details?.[0]?.account?.timezone;
  return { dateFormat, timezone };
};

export const commonDateFormatLogic = (timestamp, dateFormat, timezone) => {
  const momentObj = (time) => {
    if (time) {
      return timezone ? moment(time).tz(timezone) : moment(time);
    } else {
      return moment();
    }
  };

  const currentDate = momentObj().format(defaultDateFormat);
  const yesterday = momentObj().subtract(1, "days").format(defaultDateFormat);

  if (timestamp) {
    if (momentObj(timestamp).format(defaultDateFormat) === currentDate) {
      return `Today, ${momentObj(timestamp).format("hh:mm A")}`;
    } else if (momentObj(timestamp).format(defaultDateFormat) === yesterday) {
      return `Yesterday, ${momentObj(timestamp).format("hh:mm A")}`;
    } else if (dateFormat) {
      return momentObj(timestamp).format(dateFormat.toUpperCase() + ", h:mm A");
    } else {
      return momentObj(timestamp).format(defaultDateFormat + ", h:mm A");
    }
  }
};

export const dateTimeFormatInApp = (timestamp) => {
  const { dateFormat, timezone } = getTimeZoneAndDateFormat();
  return commonDateFormatLogic(timestamp, dateFormat, timezone);
};

export const getUnRecognizedDeviceFireStoreData = (
  userId,
  sessionId,
  setFireStoreData
) => {
  const db = getFirestore(app);
  const userRef = doc(db, "user", userId, "session", sessionId);
  try {
    onSnapshot(
      userRef,
      (querySnapshot) => {
        const data = querySnapshot.data();
        setFireStoreData(data);
        return data;
      },
      (_e) => {}
    );
  } catch (_e) {}
};

export const isPhoneVerificationRequired = (user, countries) => {
  const accountCountry = user?.accounts_details?.[0]?.account?.country;
  const accountCountryObj = getCountryObj(
    "short_name",
    accountCountry,
    countries
  );
  return (
    !user?.phone_number &&
    accountCountryObj?.phone_number_verification_starts_at
  );
};

export const isMFAVerificationRequired = (
  account,
  mfaStatus,
  countrySupportedMFA
) => {
  const mfaFlagged = account?.account?.tags?.includes("MFA_FLAGGED");

  return mfaFlagged && !mfaStatus && countrySupportedMFA;
};

export const fetchCountries = () =>
  callAPIInterface("GET", "/country?limit=300")
    .then((response) => response?.data)
    .catch((e) => e);

// set redux states to open app detail page and pass required data for the detail page in the redux state
export const handleOpenAppDetailDialogAndPassDetails = (props) => {
  store.dispatch(openAppDetailModal(true));
  store.dispatch(setAppDetails(props));
};

// set redux state to open installed app modal and pass required details
export const handleOpenAppDialog = (props) => {
  store.dispatch(openInstalledAppDialog(true));
  store.dispatch(setAppDetails(props));
};

export const categoryColors = [
  { bg: "#EBEEF4", border: "#D8DDE9" },
  { bg: "#F2E5D9", border: "#FFE0B2" },
  { bg: "#FCF6C0", border: "#FAF094" },
  { bg: "#DEF1F7", border: "#C6E7F1" },
];

export const gridColors = [
  "#7f2d91",
  "#232E94",
  "#5C2D91",
  "#C1443C",
  "#007F7F",
  "#FFAE0A",
  "#C26815",
  "#333843",
];
export const mobileAppUrl = "https://speedapp.go.link/j7BLn";

export const contactTypes = [
  { id: 1, label: "Individual", value: "individual" },
  { id: 2, label: "Business", value: "business" },
];

export const contactPaymentMethods = [
  {
    id: 1,
    label: "LN Address",
    payment_method: "lightning",
    target_currency: "SATS",
    withdraw_method: "lnurl",
  },
  {
    id: 2,
    label: "BTC address",
    payment_method: "onchain",
    target_currency: "SATS",
    withdraw_method: "onchain",
  },
  {
    id: 3,
    label: "Ethereum address",
    payment_method: "ethereum",
    target_currency: "USDT",
    withdraw_method: "ethereum",
  },
  {
    id: 4,
    label: "Tron address",
    payment_method: "tron",
    target_currency: "USDT",
    withdraw_method: "tron",
  },
  {
    id: 5,
    label: "Solana address",
    payment_method: "solana",
    target_currency: "USDC",
    withdraw_method: "solana",
  },
  {
    id: 6,
    label: "Ton address",
    payment_method: "ton",
    target_currency: "USDT",
    withdraw_method: "ton",
  },
];

export const getIcon = (paymentMethod, targetCurrency) => {
  if (paymentMethod === lightningLabel) {
    return lightningIcons[targetCurrency];
  } else if (paymentMethod === onChainLabel) {
    return bitCoinSymbol;
  } else if (paymentMethod === ethereumLabel) {
    return ethereumIcon;
  } else if (paymentMethod === tronLabel) {
    return tronIcon;
  } else if (paymentMethod === solanaLabel) {
    return solanaIcon;
  } else if (paymentMethod === tonLabel) {
    return tonIcon;
  }
};

export const mfaMethods = {
  sms: {
    title: smsAuthentication,
    icon: smsIcon,
  },
  google_authenticator: {
    title: authenticatorApp,
    icon: authAppIcon,
  },
};

export const mfaSetupOptions = [
  { id: 1, mfa_type: "google_authenticator" },
  { id: 2, mfa_type: "sms" },
];

export const planTagsColor = {
  Base: "linear-gradient(157deg, rgba(255, 255, 255, 0.00) 11.89%, rgba(0, 0, 0, 0.20) 87.91%), #DCB6AD",
  Verified:
    "linear-gradient(157deg, rgba(255, 255, 255, 0.00) 11.89%, rgba(0, 0, 0, 0.20) 87.91%), #0CAD62",
  Premium:
    "linear-gradient(157deg, rgba(255, 255, 255, 0.00) 11.89%, rgba(0, 0, 0, 0.20) 87.91%), #3829E3",
};

export const depositCurrencySupported = ["USD"];

export const paymentMethodTags = {
  lightning: {
    title: "LN Address",
    background: "#FEF6EB",
  },
  onchain: {
    title: "BTC Address",
    background: "#FEF6EB",
  },
  ethereum: {
    title: "ETH Address",
    background: "#F1F8F6",
  },
  tron: {
    title: "TRX Address",
    background: "#F1F8F6",
  },
  solana: {
    title: "SOL Address",
    background: "#EEF5FC",
  },
  ton: {
    title: "TON Address",
    background: "#EEF5FC",
  },
};

export const handleContactSupport = () => {
  window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${supportMail}`);
};

export const satsSymbol = (
  <span
    style={{
      fontFamily: "Satoshi-Symbol",
      height: "24px",
      paddingTop: "1.5px",
    }}
  >
    !
  </span>
);

export const dateFormatOptions = [
  { id: 1, label: "YYYY/MM/DD", value: "YYYY/MM/DD" },
  { id: 2, label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
  { id: 3, label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
];

export const profileSectionStyles = {
  Base: {
    textColor: "#0A193E",
    avatarBackground:
      "linear-gradient(180deg, var(--Subscription-Basic-Plan-BG, #F9F2F1) 0%, #EBD8C6 100%)",
    avatarBorder: "1px solid #DCB6AD",
    avatarTextColor: "#0A193E",
    boxBackgroundColor: "#FFFFFF",
    boxBackgroundImage: "",
  },
  Verified: {
    textColor: "#0A193E",
    avatarBackground:
      "linear-gradient(180deg, var(--Subscription-Plus-Plan-BG, #DEFDEE) 0%, #B2FAD7 100%);",
    avatarBorder: "1px solid #0CAD62",
    avatarTextColor: "#0A193E",
    boxBackgroundColor: "#FFFFFF",
    boxBackgroundImage: `url(${verifiedPlanBg})`,
  },
  Premium: {
    textColor: "#FFF",
    avatarBackground:
      "linear-gradient(180deg, var(--Subscription-Premium-Plan-BG, #EEEDFD) 0%, #D4D1FA 100%)",
    avatarBorder: "1px solid #3829E3",
    avatarTextColor: "#0A193E",
    boxBackgroundColor: "#FFFFFF",
    boxBackgroundImage: `url(${premiumPlanBg})`,
  },
};

export const removedAppStatus = ["deleted", "suspended"];

export const EVENT_TYPES = {
  VIEW: "VIEW",
  SHARED: "SHARED",
  OPEN: "OPEN",
  CLOSE: "CLOSE",
  RESTORE: "RESTORE",
};

const REQURIED_SESSION_EVENTS = [
  EVENT_TYPES.OPEN,
  EVENT_TYPES.CLOSE,
  EVENT_TYPES.RESTORE,
];

const REQURIED_CLOSE_TIME_EVENTS = [EVENT_TYPES.CLOSE, EVENT_TYPES.RESTORE];

export const commonAnalyticsAPI = async ({
  data,
  type,
  session,
  closeTime = null,
}) => {
  try {
    const payload = {
      app_id: data.id,
      app_name: data.display_name,
      event_type: EVENT_TYPES[type],
    };

    if (REQURIED_SESSION_EVENTS.includes(type)) {
      payload.app_session_id = session;
    }

    if (REQURIED_CLOSE_TIME_EVENTS.includes(type)) {
      payload.data = {
        app_close_time: closeTime,
      };
    }

    await callAPIInterface(
      "POST",
      "/manage-app/apps/events",
      JSON.stringify(payload)
    );
  } catch (e) {
    console.log("Error while calling events api:", e);
  }
};

export const handleGetEventSession = (prefix, length = 8) => {
  let timestamp = Math.floor(Date.now() / 100000);
  let id = `${prefix}_${timestamp}`;

  for (let i = 0; i < length; ++i) {
    const randomPosition = Math.round(Math.random() * CHAR_SET.length);
    id += CHAR_SET.charAt(randomPosition);
  }

  return id;
};
export const getSwapLimits = async () => {
  try {
    const res = await callAPIInterface("GET", `/balances/swap/pair/limit`);
    return res.data;
  } catch (err) {
    return [];
  }
};

export const displayCurrency = (currency) => {
  switch (currency) {
    case sats:
      return btc;
    case ggc:
      return play;
    case usd:
      return cash;
    case XAUT:
      return XAUT;
    default:
      return currency;
  }
};

export const statementPeriodOptions = [
  { id: 1, label: "Last month", value: "last_month" },
  { id: 2, label: "Last 3 months", value: "last_three_month" },
  { id: 3, label: "Last 6 months", value: "last_six_month" },
  { id: 4, label: "Custom date", value: "custom_date" },
];

export const statmentTransactionTypeOptions = [
  { label: "All", value: "all" },
  { label: "Credit", value: "CREDIT" },
  { label: "Debit", value: "DEBIT" },
];

export const getCurrenciesOptions = (accountAssets) => {
  const enabledCurrencies = prepareTabDataInOrder(accountAssets);

  const options = [{ label: "All", value: "all" }];

  enabledCurrencies.forEach((asset) => {
    if (asset?.uuid) {
      options.push({
        label: asset?.name || asset?.uuid,
        value: asset?.uuid,
      });
    }
  });

  return options;
};

export const transactionSourceOptions = [
  { id: 1, label: "Payment", value: "Payment" },
  { id: 2, label: "Payout", value: "Payout" },
  { id: 3, label: "Payout Reversal", value: "Payout Reversal" },
  { id: 4, label: "Network Fees", value: "Network Fees" },
  { id: 5, label: "Network Fees Reversal", value: "Network Fees Reversal" },
  { id: 6, label: "Withdraw", value: "Withdraw" },
  { id: 7, label: "Withdraw Reversal", value: "Withdraw Reversal" },
  { id: 8, label: "Transfer Out", value: "Transfer Out" },
  { id: 9, label: "Transfer In", value: "Transfer In" },
  { id: 10, label: "Affiliate Fee In", value: "Affiliate Fee In" },
  { id: 11, label: "Affiliate Fee Out", value: "Affiliate Fee Out" },
  { id: 12, label: "Application Fee In", value: "Application Fee In" },
  { id: 13, label: "Application Fee Out", value: "Application Fee Out" },
  { id: 14, label: "Swap In", value: "Swap In" },
  { id: 15, label: "Swap Out", value: "Swap Out" },
  { id: 16, label: "Swap Reversal", value: "Swap Reversal" },
];

export const dateOrderOptions = [
  { id: 1, label: "Ascending", value: "ascending" },
  { id: 2, label: "Descending", value: "descending" },
];

export const statmentDocumentFormatOptions = [
  { id: 1, label: "PDF", value: "pdf" },
  { id: 2, label: "XLSX", value: "xlsx" },
  { id: 3, label: "CSV", value: "csv" },
];

export const reconciliationFormat =
  "Reconciliation format(Debit/Credit amount in separate column)";

export const validateAmountRange = ({
  startAmount,
  endAmount,
  setErrorMessage,
  optionalEndAmount = false,
}) => {
  if (endAmount && !startAmount) {
    setErrorMessage?.(validStartAmountMsg);
    return false;
  } else if (!optionalEndAmount && startAmount && !endAmount) {
    setErrorMessage?.(validEndAmountMsg);
    return false;
  } else if (startAmount && endAmount) {
    const isGreaterEndAmount = +startAmount < +endAmount;
    !isGreaterEndAmount &&
      setErrorMessage?.(validEndAmountGreaterStartAmountMsg);
    return isGreaterEndAmount;
  } else return true;
};

export const transactionTypeData = [
  { id: 1, short_name: "Withdraw", label: "Sent" },
  { id: 2, short_name: "Payment", label: "Received" },
  { id: 3, short_name: "Network Fees" },
  { id: 4, short_name: "Conversion Fee" },
  { id: 5, short_name: "Withdraw Reversal", label: "Refund" },
  { id: 6, short_name: "Network fee refund" },
  { id: 7, short_name: "Swap in" },
  { id: 8, short_name: "Swap out" },
  { id: 9, short_name: "Referral bonus" },
  { id: 10, short_name: "Promo reward" },
  { id: 11, short_name: "Bounty earned" },
  { id: 12, short_name: "Deposit" },
  { id: 13, short_name: "Payout" },
  { id: 14, short_name: "Payout Reversal", label: "Payout refund" },
  { id: 15, short_name: "Task Reward" },
];

export const utcTimestampForDashboard = ({
  timezone,
  isStartDay,
  operation,
  typeOfSubstraction = "days",
  days,
}) => {
  const localDate = isStartDay
    ? moment.tz(timezone).startOf("day")
    : moment.tz(timezone).endOf("day"); // Get start or end of the day in local timezone
  const utcDate = moment.utc(localDate); // Get end of the day in utc for local timezone
  let timestamp = utcDate.valueOf();

  if (days) {
    timestamp = utcDate[operation](days, typeOfSubstraction).valueOf(); // timestamp after add or subtract days
  }

  return timestamp;
};

export const hasPaymentMethodWithModule = ({ asset, module }) => {
  if (
    asset?.supported_modules?.includes(module) &&
    asset.payment_method_details?.length != 0
  ) {
    return true;
  } else {
    return false;
  }
};

export const getSpeedFeeLabel = ({ speedFeePercentage, speedFixedFees }) => {
  if (!speedFeePercentage && speedFixedFees) {
    return speedFee;
  } else {
    const postFix =
      speedFeePercentage && speedFixedFees
        ? `(${speedFeePercentage}% + Fix)`
        : `${speedFeePercentage}%`;

    return `${speedFee} @${postFix}`;
  }
};

export const assetMaxAmountForFPJSCheck = {
  SATS: 100000,
  USDT: 100,
  USDC: 100,
  XAUT: 0.02,
};
